// FavoritesSidebarSection.jsx

import React, { useCallback } from 'react';

// Routing
import { useLocation } from 'react-router-dom';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUI } from 'context/UIContext';

// Utilities
import { getNodeWorkflowPercentComplete } from 'node/missions/mission/utils/missionRunUtils';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// Hooks
import useFavoriteNodes from 'favorites/hooks/useFavoriteNodes';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import getNodeKeyFromPath from 'main/sidebar/hooks/getNodeKeyFromPath';

// CSS
import './FavoritesSidebarSection.css';

// Components
import SidebarButton from 'main/sidebar/button/SidebarButton';
import Icon from 'svg/Icon';

const FAVORITE_LABEL_MAX = 50;

/**
 * ellipsizeLabel
 *
 * Truncates a label after max characters with an ellipsis.
 *
 * @param {string} value - Full label text.
 * @param {number} [max=18] - Max characters before ellipsis.
 * @returns {string}
 */
function ellipsizeLabel(value, max = FAVORITE_LABEL_MAX) {
	const text = String(value || '').trim();
	if (!text) return 'Untitled';
	if (text.length <= max) return text;
	return `${text.slice(0, max)}…`;
}

/**
 * FavoritesSidebarSection
 *
 * Lists the user's pinned nodes above MENU in the left sidebar.
 * Clicking a row opens that node directly in Workspace.
 *
 * @returns {ReactNode} The rendered favorites section, or null when empty.
 */
const FavoritesSidebarSection = React.memo(() => {

	// ----- CONTEXT ----- //

	const { getType } = useNodeTypes();
	const { isSidebarRailCompact } = useUI();
	const compact = Boolean(isSidebarRailCompact);

	// ----- ROUTING ----- //

	const location = useLocation();

	// ----- HOOKS ----- //

	const { rows } = useFavoriteNodes();
	const { openExpanded } = useNodeOpenIntent();

	const selectedNodeKey = getNodeKeyFromPath(location.pathname);

	// ----- HANDLERS ----- //

	/**
	 * handleFavoriteClick
	 *
	 * Opens the favorited node in Workspace.
	 *
	 * @param {object} node - Hydrated favorite node.
	 */
	const handleFavoriteClick = useCallback((node) => {
		if (!node?.key) return;
		openExpanded(node);
	}, [openExpanded]);

	// ----- RENDER ----- //

	if (!rows.length) return null;

	return (
		<div
			className={[
				'favorites-sidebar-section-container',
				compact ? 'is-rail-compact' : '',
			].filter(Boolean).join(' ')}
		>

			{/* HEADING 
			{!compact ? (
				<div className="sidebar-bucket-heading">
					<div className="sidebar-bucket-subtitle favorites-sidebar-section-title-row">
						<span>Pinned</span>
					</div>
				</div>
			) : null}
			{!compact ? (
			<div className="sidebar-bucket-heading"
				style={{
					paddingLeft: '9px',
					paddingTop: '8px',
				}}>
				<Icon
					iconKey="pin_drop_fill"
					color="var(--orange)"
					height={20}
					width={20}
					style={{
						marginRight: '8px',
					}}
				/>
			</div>
			) : null}
			 */}

			{/* LIST */}
			<div className="sidebar-bucket-list"
				style={{
					paddingLeft: '3px',
					paddingTop: '8px',
				}}>
				{rows.map((node) => {
					const fullTitle = String(node?.title || '').trim() || 'Untitled';
					const typeDef = getType(node?.type);
					const { iconKey } = resolveNodeIcon({ node, typeDef });
					const percentComplete = getNodeWorkflowPercentComplete(node);

					return (
						<SidebarButton
							key={node.key}
							iconKey={iconKey || 'bookmark'}
							label={ellipsizeLabel(fullTitle)}
							percentComplete={percentComplete}
							selected={selectedNodeKey === node.key}
							title={fullTitle}
							onClick={() => handleFavoriteClick(node)}
						/>
					);
				})}
			</div>

		</div>
	);
});

FavoritesSidebarSection.displayName = 'FavoritesSidebarSection';

export default FavoritesSidebarSection;
